import { createSelector } from 'reselect';

import {
  DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD,
  DataTerminalTransitionGraph,
  DataTerminalTransitionGraphFieldItem,
  DataTerminalTransitionGraphParams,
} from '../../domain/dataTerminalTransitionGraph';
import { buildGraphListForNormalizedFields } from '../../domain/dataTerminalTransitionGraphFields';
import { LoadingState } from '../../domain/schemas';

import { RootState } from '../../store';

// Action Types

const FETCH_DATA_TERMINAL_TRANSITION_GRAPH =
  'FETCH_DATA_TERMINAL_TRANSITION_GRAPH' as const;
const FETCH_DATA_TERMINAL_TRANSITION_GRAPH_REQUEST =
  'FETCH_DATA_TERMINAL_TRANSITION_GRAPH_REQUEST' as const;
const FETCH_DATA_TERMINAL_TRANSITION_GRAPH_SUCCESS =
  'FETCH_DATA_TERMINAL_TRANSITION_GRAPH_SUCCESS' as const;
const SEARCH_DATA_TERMINAL_TRANSITION_GRAPH =
  'SEARCH_DATA_TERMINAL_TRANSITION_GRAPH' as const;
const SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_HALL =
  'SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_HALL' as const;
const SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_DATE =
  'SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_DATE' as const;
const SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS =
  'SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS' as const;
const SET_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS =
  'SET_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS' as const;
const SET_DATA_TERMINAL_TRANSITION_GRAPH_GRAPH_LIST =
  'SET_DATA_TERMINAL_TRANSITION_GRAPH_GRAPH_LIST' as const;
const RENEW_DATA_TERMINAL_TRANSITION_GRAPH =
  'RENEW_DATA_TERMINAL_TRANSITION_GRAPH' as const;

export const DataTerminalTransitionGraphActionTypes = {
  FETCH_DATA_TERMINAL_TRANSITION_GRAPH,
  FETCH_DATA_TERMINAL_TRANSITION_GRAPH_REQUEST,
  FETCH_DATA_TERMINAL_TRANSITION_GRAPH_SUCCESS,
  SEARCH_DATA_TERMINAL_TRANSITION_GRAPH,
  SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_HALL,
  SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_DATE,
  SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
  SET_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
  SET_DATA_TERMINAL_TRANSITION_GRAPH_GRAPH_LIST,
  RENEW_DATA_TERMINAL_TRANSITION_GRAPH,
};

// Action Creators

/**
 * 推移グラフデータを取得する
 * @param params 推移グラフの検索条件
 */
function fetchDataTerminalTransitionGraphAction(
  params: DataTerminalTransitionGraphParams
) {
  return {
    type: FETCH_DATA_TERMINAL_TRANSITION_GRAPH,
    payload: { params },
  };
}

/**
 * 推移グラフ取得前に実行する
 */
function fetchDataTerminalTransitionGraphRequestAction() {
  return {
    type: FETCH_DATA_TERMINAL_TRANSITION_GRAPH_REQUEST,
  };
}

/**
 * 推移グラフデータ取得完了時データを登録する
 * @param dataTerminalTransitionGraph 取得したグラフデータ
 */
function fetchDataTerminalTransitionGraphSuccessAction(
  dataTerminalTransitionGraph: DataTerminalTransitionGraph
) {
  return {
    type: FETCH_DATA_TERMINAL_TRANSITION_GRAPH_SUCCESS,
    payload: { dataTerminalTransitionGraph },
  };
}

/**
 * 選択した店舗の推移グラフを表示する
 * @param shuGroupId 種別データ
 * @param shuList 種別データ
 * @param halls 表示する店舗コード
 * @param field 表示するグラフ項目（未指定時は既存項目を維持）
 */
function searchDataTerminalTransitionGraphAction(
  shuGroupId: string[],
  shuList: string[],
  halls: string[],
  field?: string
) {
  return {
    type: SEARCH_DATA_TERMINAL_TRANSITION_GRAPH,
    payload: { shuGroupId, shuList, halls, field },
  };
}

/**
 * 指定した店舗でグラフを絞り込む（店舗選択フォーム用）
 * @param hall 絞り込む店舗
 */
function searchDataTerminalTransitionGraphHallAction(hall: string[]) {
  return {
    type: SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_HALL,
    payload: { hall },
  };
}

/**
 * 指定した期間で推移グラフデータを再取得
 * @param startDate 開始日
 * @param endDate 終了日
 */
function searchDataTerminalTransitionGraphDateAction(
  startDate: string,
  endDate: string
) {
  return {
    type: SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_DATE,
    payload: { startDate, endDate },
  };
}

/**
 * 指定したグラフ項目で推移グラフデータを再取得
 * @param field 棒グラフのグラフ項目
 * @deprecated 複数項目表示では setDataTerminalTransitionGraphFieldsAction を使用
 */
function searchDataTerminalTransitionGraphFieldsAction(field: string) {
  return {
    type: SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
    payload: { field },
  };
}

/**
 * 表示するグラフ項目を配列で一括指定して再取得する。
 */
function setDataTerminalTransitionGraphFieldsAction(fields: string[]) {
  return {
    type: SET_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
    payload: { fields },
  };
}

/**
 * 取得済み graphList を fields に合わせて更新する（削除・未選択時）。
 */
function setDataTerminalTransitionGraphGraphListAction(fields: string[]) {
  return {
    type: SET_DATA_TERMINAL_TRANSITION_GRAPH_GRAPH_LIST,
    payload: { fields },
  };
}

/**
 * 推移グラフデータ取得失敗時にエラー内容を登録する
 */
function renewDataTerminalTransitionGraphAction() {
  return {
    type: RENEW_DATA_TERMINAL_TRANSITION_GRAPH,
  };
}

export const DataTerminalTransitionGraphActionCreators = {
  fetchDataTerminalTransitionGraphAction,
  fetchDataTerminalTransitionGraphRequestAction,
  fetchDataTerminalTransitionGraphSuccessAction,
  searchDataTerminalTransitionGraphAction,
  searchDataTerminalTransitionGraphHallAction,
  searchDataTerminalTransitionGraphDateAction,
  searchDataTerminalTransitionGraphFieldsAction,
  setDataTerminalTransitionGraphFieldsAction,
  setDataTerminalTransitionGraphGraphListAction,
  renewDataTerminalTransitionGraphAction,
};

// Actions

export type FetchDataTerminalTransitionGraphAction = ReturnType<
  typeof fetchDataTerminalTransitionGraphAction
>;
export type SearchDataTerminalTransitionGraphAction = ReturnType<
  typeof searchDataTerminalTransitionGraphAction
>;
export type SearchDataTerminalTransitionGraphHallAction = ReturnType<
  typeof searchDataTerminalTransitionGraphHallAction
>;
export type SearchDataTerminalTransitionGraphDateAction = ReturnType<
  typeof searchDataTerminalTransitionGraphDateAction
>;
export type SearchDataTerminalTransitionGraphFieldsAction = ReturnType<
  typeof searchDataTerminalTransitionGraphFieldsAction
>;
export type SetDataTerminalTransitionGraphFieldsAction = ReturnType<
  typeof setDataTerminalTransitionGraphFieldsAction
>;
export type SetDataTerminalTransitionGraphGraphListAction = ReturnType<
  typeof setDataTerminalTransitionGraphGraphListAction
>;

type DataTerminalTransitionGraphAction =
  | FetchDataTerminalTransitionGraphAction
  | SearchDataTerminalTransitionGraphAction
  | SearchDataTerminalTransitionGraphHallAction
  | SearchDataTerminalTransitionGraphDateAction
  | SearchDataTerminalTransitionGraphFieldsAction
  | SetDataTerminalTransitionGraphFieldsAction
  | SetDataTerminalTransitionGraphGraphListAction
  | ReturnType<typeof fetchDataTerminalTransitionGraphRequestAction>
  | ReturnType<typeof fetchDataTerminalTransitionGraphSuccessAction>
  | ReturnType<typeof renewDataTerminalTransitionGraphAction>;

// State

type DataTerminalTransitionGraphState = {
  dataTerminalTransitionGraph: DataTerminalTransitionGraph | undefined;
  loadingState: LoadingState;
};

const initialState: DataTerminalTransitionGraphState = {
  dataTerminalTransitionGraph: undefined,
  loadingState: 'prepare',
};

// Selector

/**
 * 全てのグラフデータを取得する
 */
export const dataTerminalTransitionGraphAllSelector = (state: RootState) =>
  state.dataTerminalTransitionGraph.dataTerminalTransitionGraph;

/**
 * グラフ項目ごとのデータ一覧を取得する
 */
export const dataTerminalTransitionGraphGraphListSelector = createSelector(
  dataTerminalTransitionGraphAllSelector,
  (graph): DataTerminalTransitionGraphFieldItem[] => graph?.graphList ?? []
);

/**
 * Redux の setting + graphList から、再取得・お気に入り保存用の params を組み立てる。
 */
export const dataTerminalTransitionGraphSettingSelector = createSelector(
  dataTerminalTransitionGraphAllSelector,
  (graph): DataTerminalTransitionGraphParams | undefined => {
    if (!graph) {
      return undefined;
    }
    const graphFields = graph.graphList.map((item) => item.field);
    const firstField =
      graphFields.at(0) ?? DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD;
    return {
      ...graph.setting,
      field: firstField,
      ...(graphFields.length > 0 ? { fields: graphFields } : {}),
    };
  }
);

/**
 * 推移グラフの検索条件を取得する（お気に入り保存・再取得用）
 */
export const dataTerminalTransitionGraphSearchConditionSelector = () =>
  createSelector(
    dataTerminalTransitionGraphSettingSelector,
    (params) => params ?? ({} as DataTerminalTransitionGraphParams)
  );

/**
 * ローディング状態を取得する
 */
export const dataTerminalTransitionGraphLoadingStateSelector = (
  state: RootState
) => state.dataTerminalTransitionGraph.loadingState;

export function dataTerminalTransitionGraphReducer(
  state = initialState,
  action: DataTerminalTransitionGraphAction
): DataTerminalTransitionGraphState {
  switch (action.type) {
    case FETCH_DATA_TERMINAL_TRANSITION_GRAPH_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_TERMINAL_TRANSITION_GRAPH_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        dataTerminalTransitionGraph: action.payload.dataTerminalTransitionGraph,
      };
    case SET_DATA_TERMINAL_TRANSITION_GRAPH_GRAPH_LIST: {
      const graph = state.dataTerminalTransitionGraph;
      if (!graph) {
        return state;
      }
      const nextGraphList = buildGraphListForNormalizedFields(
        graph.graphList,
        action.payload.fields
      );
      return {
        ...state,
        dataTerminalTransitionGraph: {
          ...graph,
          graphList: nextGraphList,
        },
      };
    }
    case RENEW_DATA_TERMINAL_TRANSITION_GRAPH:
      return initialState;
    default:
      return state;
  }
}
